import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import PlaceMedal from './PlaceMedal';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL || '';
const POLL_MS = 15000;
const PLACE_KEY = { 1: 'game.firstPlace', 2: 'game.secondPlace', 3: 'game.thirdPlace' };
const MEDAL_EMOJI = { 1: '🥇', 2: '🥈', 3: '🥉' };

/**
 * Floating strip of up to 3 medals showing the current winners of a game.
 * Polls /api/games/{gameId}/winners and emits a toast + glow animation when a
 * new prize place is taken while the user is on the page.
 *
 * Props:
 *   gameId         (required)
 *   currentUserId  – highlights "you" if your own id matches a winner
 *   anchor         – CSS positioning. Default: fixed top-right under the user avatar.
 *   inline         – if true, render inline (for embedding in flowing layouts)
 *   variant        – 'pill' (default, compact pill background) | 'bare' (no
 *                    background, larger medals; clicking an occupied medal opens
 *                    the winner's results page, matching the home-page game cards)
 */
export default function GameWinnersStrip({
  gameId,
  currentUserId = null,
  anchor = { position: 'fixed', top: '54px', right: '0.75rem', zIndex: 9990 },
  inline = false,
  variant = 'pill',
}) {
  const [winners, setWinners] = useState([]); // [{place, user_id, user_full_name, percentage}]
  const [animatingPlaces, setAnimatingPlaces] = useState(() => new Set());
  const prevSigRef = useRef(null); // signature of previous winners list
  const navigate = useNavigate();
  const { t } = useLanguage();
  const placeLabel = (place) => t(PLACE_KEY[place]);

  const fetchWinners = useCallback(async () => {
    if (!gameId) return;
    try {
      const r = await axios.get(`${API}/api/games/${gameId}/winners`);
      const data = Array.isArray(r.data) ? r.data : [];
      // Sort by place asc so [1,2,3]
      data.sort((a, b) => (a.place || 99) - (b.place || 99));

      // Diff against prev snapshot
      const sig = data.map(w => `${w.place}:${w.user_id}`).join('|');
      if (prevSigRef.current !== null && prevSigRef.current !== sig) {
        const prevMap = {};
        (prevSigRef.current || '').split('|').filter(Boolean).forEach(p => {
          const [place, uid] = p.split(':');
          prevMap[place] = uid;
        });
        data.forEach(w => {
          const prevUid = prevMap[String(w.place)];
          if (prevUid !== w.user_id) {
            // Either a brand-new place taken OR replaced (rare). Notify + animate.
            const isMe = currentUserId && w.user_id === currentUserId;
            const name = isMe ? t('common.you') : (w.user_full_name || t('common.player'));
            const emoji = MEDAL_EMOJI[w.place] || '🏆';
            toast.success(`${emoji} ${name} — ${placeLabel(w.place)}`, {
              description: `${t('game.accuracy')}: ${(w.percentage || 0).toFixed(1)}%`,
              duration: 6000,
            });
            setAnimatingPlaces(prev => {
              const next = new Set(prev);
              next.add(w.place);
              return next;
            });
            // Clear glow after 5s
            setTimeout(() => {
              setAnimatingPlaces(prev => {
                const next = new Set(prev);
                next.delete(w.place);
                return next;
              });
            }, 5000);
          }
        });
      }
      prevSigRef.current = sig;
      setWinners(data);
    } catch (e) {
      // Silent
    }
  }, [gameId, currentUserId]);

  useEffect(() => {
    fetchWinners();
    const t = setInterval(fetchWinners, POLL_MS);
    return () => clearInterval(t);
  }, [fetchWinners]);

  if (!gameId) return null;

  const byPlace = {};
  winners.forEach(w => { byPlace[w.place] = w; });

  const isBare = variant === 'bare';
  const medalHeight = isBare ? 56 : 28;
  const slotWidth = isBare ? 40 : 32;
  const slotHeight = isBare ? 60 : 32;

  const wrapperStyle = inline
    ? { display: 'inline-flex', alignItems: 'center', gap: isBare ? 0 : 4 }
    : isBare
    ? {
        ...anchor,
        display: 'flex',
        alignItems: 'center',
        gap: 0,
      }
    : {
        ...anchor,
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        background: 'rgba(255,255,255,0.92)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        padding: '4px 6px',
        borderRadius: 999,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        border: '1px solid rgba(0,0,0,0.06)',
      };

  return (
    <>
      <style>{`
        @keyframes gameWinnersGlow {
          0%   { box-shadow: 0 0 0 0 rgba(245,158,11,0.85), 0 0 0 0 rgba(245,158,11,0.4); transform: scale(1); }
          50%  { box-shadow: 0 0 0 8px rgba(245,158,11,0.25), 0 0 18px 6px rgba(245,158,11,0.55); transform: scale(1.10); }
          100% { box-shadow: 0 0 0 0 rgba(245,158,11,0); transform: scale(1); }
        }
        @keyframes gameWinnersBounce {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-3px); }
        }
        .gws-slot { transition: transform 0.2s ease; }
        .gws-slot:hover { transform: scale(1.08); }
        .gws-slot.gws-anim { animation: gameWinnersGlow 1.4s ease-in-out 0s 3, gameWinnersBounce 0.55s ease-in-out 0s 5; }
      `}</style>
      <div
        data-testid={`game-winners-strip-${gameId}`}
        style={wrapperStyle}
      >
        {[1, 2, 3].map(place => {
          const w = byPlace[place];
          const taken = !!w;
          const isMe = taken && currentUserId && w.user_id === currentUserId;
          const animating = animatingPlaces.has(place);
          const tooltip = taken
            ? `${placeLabel(place)}: ${isMe ? t('common.you') : (w.user_full_name || t('common.player'))}`
            : `${placeLabel(place)} — ${t('game.notTakenYet')}`;
          const clickable = taken && !!w.user_id;
          const handleClick = clickable
            ? (e) => {
                e.stopPropagation();
                navigate(`/${gameId}/user-results/${w.user_id}`);
              }
            : undefined;
          return (
            <span
              key={place}
              className={`gws-slot ${animating ? 'gws-anim' : ''}`}
              data-testid={`game-winner-medal-${place}${taken ? '-taken' : '-free'}${isMe ? '-me' : ''}`}
              title={tooltip}
              role={clickable ? 'button' : undefined}
              tabIndex={clickable ? 0 : undefined}
              onClick={handleClick}
              onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') handleClick(e); } : undefined}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: slotWidth,
                height: slotHeight,
                borderRadius: isBare ? 0 : '50%',
                background: isBare ? 'transparent' : (taken ? 'transparent' : 'rgba(0,0,0,0.06)'),
                outline: isMe && !isBare ? '2px solid #f59e0b' : 'none',
                outlineOffset: 1,
                opacity: taken ? 1 : (isBare ? 0.55 : 0.45),
                filter: taken ? 'none' : 'grayscale(0.6)',
                cursor: clickable ? 'pointer' : 'default',
              }}
            >
              <PlaceMedal place={place} height={medalHeight} title={tooltip} />
            </span>
          );
        })}
      </div>
    </>
  );
}
